import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { Router } from '@angular/router'
import { Observable, of, Subject, Subscription } from 'rxjs'
import { finalize, first, switchMap, takeUntil, tap } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import { AccessTemplatesDto, ApplicationType, SyncPairInfo } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { handleArpioError } from 'src/utils/handle-error'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { AccountService } from 'src/app/account/services/account.service'
import { ApplicationCreatorState } from 'src/app/account/services/application-creator-state.service'
import { AccessScope, AccessService } from 'src/app/services/access.service'
import { RegionsService } from 'src/app/services/regions.service'
import { stackSetsModeActive, templatesOutOfDateOnly } from 'src/app/account/services/outdated-templates'
import { SyncPairAccessCache } from 'src/app/account/services/sync-pair-access-cache.service'

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'application-creator-connect-page',
  templateUrl: './application-creator-connect-page.component.html',
  styleUrls: ['./application-creator-connect-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ApplicationCreatorConnectPageComponent implements OnInit, OnDestroy {
  sourceAccessService: AccessService = null
  targetAccessService: AccessService = null
  private initialValidationSubscription: Subscription = null
  private destroy$ = new Subject<void>()
  sourceAccessTemplate: string = null
  targetAccessTemplate: string = null
  sourceInstallCommand: string = null
  targetInstallCommand: string = null
  Scope = AccessScope

  // StackSets data
  sourceStackSetTemplateUrl: string = null
  targetStackSetTemplateUrl: string = null
  sourceTemplateBucket: string = null
  sourceTemplateKey: string = null
  targetTemplateBucket: string = null
  targetTemplateKey: string = null
  regenerationJobId: string | null = null
  initFailed = false

  constructor(
    private applicationCreatorState: ApplicationCreatorState,
    private apiService: ApiService,
    public uiService: UiService,
    private accountService: AccountService,
    private router: Router,
    private regionsService: RegionsService,
    private accessCache: SyncPairAccessCache
  ) {}

  get applicationType() {
    return this.applicationCreatorState.applicationType
  }

  get sourceEndpointUri() {
    return this.applicationCreatorState.sourceEndpointUri
  }

  get targetEndpointUri() {
    return this.applicationCreatorState.targetEndpointUri
  }

  get sourceAccountId() {
    return this.applicationCreatorState.sourceAccountId
  }

  get targetAccountId() {
    return this.applicationCreatorState.targetAccountId
  }

  get sourceRegion() {
    return this.applicationCreatorState.sourceRegion
  }

  get targetRegion() {
    return this.applicationCreatorState.targetRegion
  }

  get identityProviderId() {
    return this.applicationCreatorState.identityProviderId
  }

  get accountId() {
    return this.accountService.accountId
  }

  get loading() {
    if (this.initFailed) {
      return false
    }
    return !this.sourceAccessService || subscriptionActive(this.initialValidationSubscription)
  }

  ngOnInit() {
    // Wait for account to be loaded before validating state
    // This prevents redirect on page refresh when account data is still loading
    this.accountService.accountIdStream.pipe(first(), takeUntil(this.destroy$)).subscribe((loadedAccountId) => {
      this.initializeAfterAccountLoad(loadedAccountId)
    })
  }

  private initializeAfterAccountLoad(loadedAccountId: string): void {
    // send them back to the start if we got here without account IDs and regions
    if (!this.applicationCreatorState.validAccountsAndRegions(this.regionsService)) {
      this.router.navigateByUrl(this.uiService.buildNewApplicationUrl(loadedAccountId))
      return
    }

    let initialObservable: Observable<AccessTemplatesDto>
    const requestDto = this as SyncPairInfo
    // The template-choice step may have just run this check for us. Reusing its
    // result is what keeps the update path down to a single connectivity wait.
    const cached = this.accessCache.get(requestDto)
    const syncPairAccess = cached
      ? of(cached)
      : this.apiService
          .testSyncPairAccess(requestDto)
          .pipe(tap((accessResult) => this.accessCache.put(requestDto, accessResult)))
    initialObservable = syncPairAccess.pipe(
      switchMap((accessResult) => {
        this.sourceAccessService = new AccessService(
          this.apiService,
          this.uiService,
          requestDto,
          AccessScope.SOURCE,
          accessResult
        )
        this.targetAccessService = new AccessService(
          this.apiService,
          this.uiService,
          requestDto,
          AccessScope.TARGET,
          accessResult
        )
        // Skip fetching templates only if access is properly configured for the selected method
        // For StackSets flow, we need templates even if CloudFormation access exists
        const sourceGood = this.sourceAccessService.allGood
        const targetGood = this.targetAccessService.allGood
        const stackSetsMode = this.applicationCreatorState.accessMethod === 'stacksets'
        const sourceManagedByStackSet = this.sourceAccessService.managedByStackSet
        const targetManagedByStackSet = this.targetAccessService.managedByStackSet

        const canSkipTemplates = stackSetsMode
          ? sourceGood && targetGood && sourceManagedByStackSet && targetManagedByStackSet
          : sourceGood && targetGood

        // The choice step renders none of these templates, and a behind pair can never
        // satisfy canSkipTemplates, so deciding here saves a wasted round trip.
        if (canSkipTemplates || this.shouldOfferTemplateChoice) {
          return of(null as AccessTemplatesDto)
        }
        return this.apiService.getAccessTemplates(
          this.accountId,
          this.sourceEndpointUri,
          this.targetEndpointUri,
          this.identityProviderId
        )
      }),
      tap((accessTemplateDto) => {
        if (!!accessTemplateDto) {
          this.sourceAccessTemplate = accessTemplateDto.sourceTemplateS3Url
          this.targetAccessTemplate = accessTemplateDto.targetTemplateS3Url
          this.sourceInstallCommand = accessTemplateDto.sourceInstallCommand
          this.targetInstallCommand = accessTemplateDto.targetInstallCommand

          // StackSets data
          this.sourceStackSetTemplateUrl = accessTemplateDto.stackSetsTemplate?.sourceStackSetTemplateS3Url
          this.targetStackSetTemplateUrl = accessTemplateDto.stackSetsTemplate?.targetStackSetTemplateS3Url
          this.sourceTemplateBucket = accessTemplateDto.stackSetsTemplate?.sourceTemplate?.bucket
          this.sourceTemplateKey = accessTemplateDto.stackSetsTemplate?.sourceTemplate?.key
          this.targetTemplateBucket = accessTemplateDto.stackSetsTemplate?.targetTemplate?.bucket
          this.targetTemplateKey = accessTemplateDto.stackSetsTemplate?.targetTemplate?.key
          this.regenerationJobId = accessTemplateDto.stackSetsTemplate?.jobId ?? null
        }
      })
    )

    this.initialValidationSubscription = initialObservable
      .pipe(finalize(() => (this.initialValidationSubscription = null)))
      .subscribe(
        () => {},
        (error) => {
          // Without this the spinner never clears: `loading` keys off
          // sourceAccessService, which an early failure leaves null forever.
          this.initFailed = true
          handleArpioError(window.location.href, this.apiService, this.uiService)(error)
        },
        () => {
          // A pair that's only behind on template version belongs on the choice step,
          // not here and not skipped ahead to resources.
          if (this.shouldOfferTemplateChoice) {
            // Replaces this page in history so BACK from the choice step reaches the
            // details step instead of bouncing forward through here again.
            this.router.navigateByUrl(this.uiService.buildNewApplicationTemplateChoiceUrl(this.accountId), {
              replaceUrl: true,
            })
            return
          }
          if (this.allAccessCurrent) {
            this.router.navigateByUrl(this.uiService.buildNewApplicationResourcesUrl(this.accountId))
          }
        }
      )
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  retryAccessCheck(): void {
    this.initFailed = false
    this.accessCache.clear()
    this.initializeAfterAccountLoad(this.accountId)
  }

  backButtonClick() {
    // Must match the choice step's own render condition, not the recorded choice: once
    // the customer updates the templates that step redirects straight back here, which
    // would eat the history entry and strand them.
    if (this.outdatedTemplatesAllowed) {
      this.router.navigateByUrl(this.uiService.buildNewApplicationTemplateChoiceUrl(this.accountId))
      return
    }
    this.router.navigateByUrl(this.uiService.buildNewApplicationUrl(this.accountId))
  }

  continueButtonClick() {
    this.router.navigateByUrl(this.uiService.buildNewApplicationResourcesUrl(this.accountId))
  }

  get loadingText(): string {
    if (this.applicationType === ApplicationType.AWS) {
      return 'Checking for AWS connectivity'
    } else if (this.applicationType === ApplicationType.AZURE) {
      return 'Checking for Azure connectivity'
    } else {
      return 'Validating access...'
    }
  }

  get stackSetsEnabled(): boolean {
    return stackSetsModeActive(this.applicationType, this.applicationCreatorState.accessMethod, this.accountService)
  }

  get isTestingSourceAccess(): boolean {
    return this.sourceAccessService?.subscription !== null && !this.sourceAccessService?.subscription?.closed
  }

  get isTestingTargetAccess(): boolean {
    return this.targetAccessService?.subscription !== null && !this.targetAccessService?.subscription?.closed
  }

  // Access works AND both templates are current, per the selected access method
  // (StackSets additionally requires StackSet-managed access; CloudFormation
  // accepts any stack).  Gates the auto-redirect, so a pair that's only behind on
  // template version still lands here to see the advisory.
  get allAccessCurrent(): boolean {
    const sourceGood = this.sourceAccessService?.allGood ?? false
    const targetGood = this.targetAccessService?.allGood ?? false

    if (this.stackSetsEnabled) {
      // StackSets flow: require StackSet-managed access
      const sourceManagedByStackSet = this.sourceAccessService?.managedByStackSet ?? false
      const targetManagedByStackSet = this.targetAccessService?.managedByStackSet ?? false
      return sourceGood && targetGood && sourceManagedByStackSet && targetManagedByStackSet
    }

    // CloudFormation flow: any working access is fine
    return sourceGood && targetGood
  }

  // Gates the CONTINUE button. Back to the strict check: a customer only reaches this
  // page by asking to update, so proceeding on the deployed version is the choice
  // step's job, not this one's. BACK returns there for anyone who changes their mind.
  get accessConfigured(): boolean {
    return this.allAccessCurrent
  }

  get outdatedTemplatesReuseEnabled(): boolean {
    return this.accountService.entitledForCapability('enableOutdatedTemplatesReuse')
  }

  get templatesOutOfDateOnly(): boolean {
    return templatesOutOfDateOnly(this.sourceAccessService, this.targetAccessService, this.stackSetsEnabled)
  }

  get outdatedTemplatesAllowed(): boolean {
    return this.outdatedTemplatesReuseEnabled && this.templatesOutOfDateOnly
  }

  // The choice belongs to the customer, so it is offered on every arrival here
  // except the one where they already asked to update.
  get shouldOfferTemplateChoice(): boolean {
    return this.outdatedTemplatesAllowed && this.applicationCreatorState.templateChoice !== 'update'
  }

  // Per-side, because the badges mark only the environment that is actually behind.
  // The choice step carries the copy that explains the difference between them.
  get sourceTemplateBehind(): boolean {
    return !(this.sourceAccessService?.hasLatestVersion ?? true)
  }

  get targetTemplateBehind(): boolean {
    return !(this.targetAccessService?.hasLatestVersion ?? true)
  }

  // Azure's primary access template does an `existing` lookup of the recovery
  // subscription's delegate identity, so the recovery stack must be deployed first.
  // `accessUsable` is exactly that precondition; `allGood` additionally demands the
  // latest change set, which the deployment does not need.
  get azureRecoverySetupPending(): boolean {
    const target = this.targetAccessService
    if (this.outdatedTemplatesReuseEnabled) {
      return !(target?.accessUsable ?? false)
    }
    return !(target?.allGood ?? false)
  }

  get sourceConnectedOnOlderTemplate(): boolean {
    return this.outdatedTemplatesAllowed && this.sourceTemplateBehind
  }

  get targetConnectedOnOlderTemplate(): boolean {
    return this.outdatedTemplatesAllowed && this.targetTemplateBehind
  }

  // True when both endpoints are already managed by StackSets, so the 3-step
  // setup flow is unnecessary. The update-only component handles whatever's
  // left (e.g. template out-of-date because another app shares the same
  // source/target); if nothing is left, the page redirects on its own.
  // Mirrors application-editor's `needsStackSetsSetup` logic.
  get stackSetsSetupComplete(): boolean {
    if (!this.stackSetsEnabled) return false
    const sourceManaged = this.sourceAccessService?.managedByStackSet ?? false
    const targetManaged = this.targetAccessService?.managedByStackSet ?? false
    return sourceManaged && targetManaged
  }

  // Every TEST ACCESS button routes through here so the cache can't outlive the answer
  // it holds: validateAccess() refreshes only its own scope, so a surviving entry would
  // replay the pre-update state on the next page that reads it.
  onTestAccess(scope: 'source' | 'target'): void {
    this.accessCache.clear()
    if (scope === 'source') {
      this.sourceAccessService?.validateAccess()
    } else {
      this.targetAccessService?.validateAccess()
    }
  }
}
